import React, { useState, useEffect } from 'react';
import {
    Box,
    Typography,
    Paper,
    Grid,
    TextField,
    MenuItem,
    Button,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Checkbox,
    FormControlLabel,
    CircularProgress
} from '@mui/material';
import { toast } from 'react-toastify';
import {
    academicYearAPI,
    classAPI,
    studentAPI,
    subjectAPI,
    semesterAPI,
    examAPI // Assuming we use semester/exam interchangeably or have Exam model linked
} from '../services/api';

// Note: In Phase 1 we created Semesters. We might not have a separate 'Exam' drop down if we treat Semester as the Exam event, 
// OR we likely have an specific 'Exam' entity (Mid-Term, Unit Test 1) linked to Semester.
// Based on models, we have `Exam` model. Let's use it.

const MarksEntry = () => {
    const [academicYears, setAcademicYears] = useState([]);
    const [classes, setClasses] = useState([]);
    const [subjects, setSubjects] = useState([]);
    const [exams, setExams] = useState([]);

    // Selections
    const [selectedYear, setSelectedYear] = useState('');
    const [selectedClass, setSelectedClass] = useState('');
    const [selectedSubject, setSelectedSubject] = useState('');
    const [selectedExam, setSelectedExam] = useState('');

    // Data
    const [students, setStudents] = useState([]);
    const [marksData, setMarksData] = useState({}); // Map studentId -> { marksObtained, isPresent, remarks }
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        const loadInitialData = async () => {
            try {
                const years = await academicYearAPI.getAll();
                setAcademicYears(years.data.data || []);
                const examsRes = await examAPI.getExams(); // Should filter by Year in real app
                setExams(examsRes.data.data || []);
            } catch (error) {
                console.error("Failed to load init data");
            }
        };
        loadInitialData();
    }, []);

    useEffect(() => {
        if (selectedYear) {
            classAPI.getAll({ academicYearId: selectedYear }).then(res => setClasses(res.data.data || []));
        }
    }, [selectedYear]);

    useEffect(() => {
        if (selectedClass && selectedYear) {
            subjectAPI.getAll({ classId: selectedClass, academicYearId: selectedYear }).then(res => setSubjects(res.data.data || []));
        }
    }, [selectedClass, selectedYear]);

    const handleFetchStudents = async () => {
        if (!selectedClass || !selectedSubject || !selectedExam) {
            toast.warning("Please select Class, Subject and Exam");
            return;
        }

        setLoading(true);
        try {
            // 1. Get Students
            const studentsRes = await studentAPI.getAll({
                classId: selectedClass,
                academicYearId: selectedYear,
                status: 'active'
            });
            const studentList = studentsRes.data.data || [];

            // 2. Get Existing Results
            const resultsRes = await examAPI.getResults({
                examId: selectedExam,
                subjectId: selectedSubject
            });
            const existingResults = resultsRes.data.data || [];

            // 3. Merge
            const selectedSubjectObj = subjects.find(s => String(s.id) === String(selectedSubject));
            const subjectTotalMarks = selectedSubjectObj ? Number(selectedSubjectObj.totalMarks) || 100 : 100;

            const initialMarks = {};
            studentList.forEach(student => {
                const result = existingResults.find(r => r.studentId === student.id);
                if (result) {
                    initialMarks[student.id] = {
                        marksObtained: result.marksObtained,
                        totalMarks: result.totalMarks,
                        isPresent: result.isPresent !== undefined ? result.isPresent : true,
                        remarks: result.remarks || ''
                    };
                } else {
                    initialMarks[student.id] = {
                        marksObtained: '',
                        totalMarks: subjectTotalMarks,
                        isPresent: true,
                        remarks: ''
                    };
                }
            });

            setStudents(studentList);
            setMarksData(initialMarks);

        } catch (error) {
            toast.error("Failed to fetch data");
        } finally {
            setLoading(false);
        }
    };

    const handleMarkChange = (studentId, field, value) => {
        setMarksData(prev => ({
            ...prev,
            [studentId]: {
                ...prev[studentId],
                [field]: value
            }
        }));
    };

    const handleSave = async () => {
        const payload = {
            marks: students.map(student => ({
                examId: selectedExam,
                studentId: student.id,
                subjectId: selectedSubject,
                marksObtained: marksData[student.id].marksObtained || 0,
                totalMarks: marksData[student.id].totalMarks,
                isPresent: marksData[student.id].isPresent,
                remarks: marksData[student.id].remarks
            }))
        };

        try {
            await examAPI.bulkUpdateMarks(payload);
            toast.success("Marks saved successfully");
        } catch (error) {
            toast.error("Failed to save marks");
        }
    };

    return (
        <Box sx={{ p: 3 }}>
            <Typography variant="h5" gutterBottom>Marks Entry</Typography>

            <Paper sx={{ p: 2, mb: 3 }}>
                <Grid container spacing={2} alignItems="center">
                    <Grid item xs={12} md={3}>
                        <TextField
                            select fullWidth label="Academic Year"
                            value={selectedYear} onChange={(e) => setSelectedYear(e.target.value)}
                        >
                            {academicYears.map(y => <MenuItem key={y.id} value={y.id}>{y.name}</MenuItem>)}
                        </TextField>
                    </Grid>
                    <Grid item xs={12} md={3}>
                        <TextField
                            select fullWidth label="Exam"
                            value={selectedExam} onChange={(e) => setSelectedExam(e.target.value)}
                        >
                            {exams.map(e => <MenuItem key={e.id} value={e.id}>{e.name}</MenuItem>)}
                        </TextField>
                    </Grid>
                    <Grid item xs={12} md={3}>
                        <TextField
                            select fullWidth label="Class"
                            value={selectedClass} onChange={(e) => setSelectedClass(e.target.value)}
                            disabled={!selectedYear}
                        >
                            {classes.map(c => <MenuItem key={c.id} value={c.id}>{c.name}</MenuItem>)}
                        </TextField>
                    </Grid>
                    <Grid item xs={12} md={3}>
                        <TextField
                            select fullWidth label="Subject"
                            value={selectedSubject} onChange={(e) => setSelectedSubject(e.target.value)}
                            disabled={!selectedClass}
                        >
                            {subjects.map(s => <MenuItem key={s.id} value={s.id}>{s.name} ({s.code})</MenuItem>)}
                        </TextField>
                    </Grid>
                    <Grid item xs={12}>
                        <Button variant="contained" onClick={handleFetchStudents} disabled={!selectedSubject}>
                            Fetch Students
                        </Button>
                    </Grid>
                </Grid>
            </Paper>

            {loading ? <CircularProgress /> : students.length > 0 && (
                <Paper sx={{ p: 2 }}>
                    <TableContainer>
                        <Table>
                            <TableHead>
                                <TableRow>
                                    <TableCell>Roll No</TableCell>
                                    <TableCell>Name</TableCell>
                                    <TableCell>Present?</TableCell>
                                    <TableCell>Marks</TableCell>
                                    <TableCell>Remarks</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {students.map(student => {
                                    const data = marksData[student.id] || {};
                                    return (
                                        <TableRow key={student.id}>
                                            <TableCell>{student.rollNumber}</TableCell>
                                            <TableCell>{student.firstName} {student.lastName}</TableCell>
                                            <TableCell>
                                                <Checkbox
                                                    checked={data.isPresent}
                                                    onChange={(e) => handleMarkChange(student.id, 'isPresent', e.target.checked)}
                                                />
                                            </TableCell>
                                            <TableCell>
                                                <TextField
                                                    type="number"
                                                    disabled={!data.isPresent}
                                                    value={data.marksObtained}
                                                    onChange={(e) => handleMarkChange(student.id, 'marksObtained', e.target.value)}
                                                    size="small"
                                                    sx={{ width: 100 }}
                                                />
                                            </TableCell>
                                            <TableCell>
                                                <TextField
                                                    value={data.remarks}
                                                    onChange={(e) => handleMarkChange(student.id, 'remarks', e.target.value)}
                                                    size="small"
                                                    placeholder="Optional"
                                                />
                                            </TableCell>
                                        </TableRow>
                                    );
                                })}
                            </TableBody>
                        </Table>
                    </TableContainer>
                    <Box sx={{ mt: 2, display: 'flex', justifyContent: 'flex-end' }}>
                        <Button variant="contained" color="primary" onClick={handleSave}>
                            Save All Marks
                        </Button>
                    </Box>
                </Paper>
            )}
        </Box>
    );
};

export default MarksEntry;
